import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import GlobalSettings from './GlobalSettings';
import Branding from './Branding';
import HallucinationsSection from './HallucinationsSection';
import ApiKeyManagement from './ApiKeyManagement';
import { Globe, Radio, ImagePlay, EyeOff, KeyRound } from 'lucide-react';
import { SettingsSubnav, SettingsSubnavTab } from './SettingsSubnav';
import {
  SettingsPageHero,
  SettingsSectionWidth,
  settingsMainCardClass,
} from './SettingsSectionLayout';

const CONFIG_TABS = [
  { id: 'display-language', label: 'Display & Language', shortLabel: 'Display', Icon: Globe },
  { id: 'transcription-services', label: 'Transcription Services', shortLabel: 'Transcription', Icon: Radio },
  { id: 'branding', label: 'Branding', shortLabel: 'Branding', Icon: ImagePlay },
  { id: 'audio-post-processing', label: 'Audio Post processing', shortLabel: 'Audio', Icon: EyeOff },
  { id: 'api-keys', label: 'API Keys', shortLabel: 'API Keys', Icon: KeyRound },
];

const VALID_GLOBAL_TABS = CONFIG_TABS.map((t) => t.id);

const GlobalSettingsSection = ({
  globalSettings,
  handleGlobalChange,
  isDarkMode,
  timezone,
  timeFormat,
  setTimeFormat,
  reverseSort,
  setReverseSort,
  user,
  edgeServerEndpoint,
  showToast,
  handleBackupNow,
  keywords,
  newKeyword,
  setNewKeyword,
  handleAddKeyword,
  handleRemoveKeyword
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('display-language');

  useEffect(() => {
    const globalTab = searchParams.get('globalTab');
    if (globalTab && VALID_GLOBAL_TABS.includes(globalTab)) {
      setActiveTab(globalTab);
    } else {
      setActiveTab('display-language');
    }
  }, [searchParams]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: 'global', globalTab: tabId });
  };

  const renderSection = () => (
    <GlobalSettings
      globalSettings={globalSettings}
      handleGlobalChange={handleGlobalChange}
      isDarkMode={isDarkMode}
      timezone={timezone}
      timeFormat={timeFormat}
      setTimeFormat={setTimeFormat}
      reverseSort={reverseSort}
      setReverseSort={setReverseSort}
      user={user}
      edgeServerEndpoint={edgeServerEndpoint}
      activeSection={activeTab}
    />
  );

  const card = settingsMainCardClass(isDarkMode);

  return (
    <SettingsSectionWidth>
      <SettingsPageHero
        isDarkMode={isDarkMode}
        title="Configurations"
        description="Configure system-wide defaults for language, transcription, branding, audio processing, and API keys."
        icon={<span className="material-symbols-outlined text-2xl">settings_input_component</span>}
      />

      <div className={`rounded-xl border p-6 md:p-8 ${card}`}>
        <SettingsSubnav isDarkMode={isDarkMode} embedded aria-label="Configuration sections">
          {CONFIG_TABS.map(({ id, label, shortLabel, Icon }) => (
            <SettingsSubnavTab
              key={id}
              isDarkMode={isDarkMode}
              active={activeTab === id}
              onClick={() => handleTabChange(id)}
              className="flex max-w-max shrink-0 items-center gap-1 md:gap-2"
            >
              <Icon className="h-4 w-4 shrink-0 md:h-5 md:w-5" />
              <span className="hidden sm:inline">{label}</span>
              <span className="sm:hidden">{shortLabel}</span>
            </SettingsSubnavTab>
          ))}
        </SettingsSubnav>

        {activeTab === 'branding' && (
          <Branding
            isDarkMode={isDarkMode}
            edgeServerEndpoint={edgeServerEndpoint}
          />
        )}
        {activeTab === 'audio-post-processing' && (
          <HallucinationsSection
            keywords={keywords}
            newKeyword={newKeyword}
            setNewKeyword={setNewKeyword}
            handleAddKeyword={handleAddKeyword}
            handleRemoveKeyword={handleRemoveKeyword}
            globalSettings={globalSettings}
            handleGlobalChange={handleGlobalChange}
            isDarkMode={isDarkMode}
            edgeServerEndpoint={edgeServerEndpoint}
          />
        )}
        {activeTab === 'api-keys' && (
          <ApiKeyManagement
            isDarkMode={isDarkMode}
            showToast={showToast}
            user={user}
          />
        )}
        {activeTab !== 'branding' && activeTab !== 'audio-post-processing' && activeTab !== 'api-keys' && (
          renderSection()
        )}
      </div>
    </SettingsSectionWidth>
  );
};

export default GlobalSettingsSection;
